import type { DeliveryStatus } from "@/infrastructure/supabase/database.types";

// --- B2C implementation board (migration 0138) ------------------------------
// Its own tables, separate from the MVP board: stage → workstream → iteration
// (one marathon each) → tasks.

/** planned → marathon (running) → feedback (output delivered) → merged */
export type IterationStatus = "planned" | "marathon" | "feedback" | "merged";

/** Same vocabulary as the MVP board's delivery statuses. */
export type B2cTaskStatus = DeliveryStatus;

/** must = needed on 1 December; should = wanted then; after = after cutover (migration 0141). */
export type B2cTaskPriority = "must" | "should" | "after";

export type B2cTaskKind = "add" | "enhance" | "reconfigure" | "carry-over" | "decision" | "e2e";

interface Timestamps {
  created_at: string;
  updated_at: string;
}

export interface B2cStageRow extends Timestamps {
  key: string;
  number: number;
  title: string;
  summary: string | null;
  gate_rule: string | null;
  start_date: string | null;
  end_date: string | null;
  sort_order: number;
}

export interface B2cWorkstreamRow extends Timestamps {
  key: string;
  code: string;
  title: string;
  summary: string | null;
  stage_key: string | null;
  owner_name: string | null;
  /** The business chain the workstream's end-to-end proof walks through. */
  e2e_proof: string | null;
  third_parties: string[];
  start_date: string | null;
  end_date: string | null;
  sort_order: number;
}

/** A workstream may start once every `builds_on_key` workstream has merged. */
export interface B2cWorkstreamGateRow {
  workstream_key: string;
  builds_on_key: string;
  created_at: string;
}

export interface B2cIterationRow extends Timestamps {
  id: string;
  workstream_key: string;
  number: number;
  status: IterationStatus;
  goal: string | null;
  /** The marathon output document (one per workstream, updated each iteration). */
  output_url: string | null;
  marathon_started_at: string | null;
  marathon_finished_at: string | null;
  merged_at: string | null;
}

/** The repos a marathon can change, each on its own integration branch (migration 0144). */
export const B2C_REPOS = ["yango-app", "yango-customizations", "yango-unopim"] as const;
export type B2cRepo = (typeof B2C_REPOS)[number];

/**
 * One repo's integration branch of an iteration, and its PR. The iteration is merged
 * once every one of its branches has merged (enforced by the database, 0144).
 */
export interface B2cIterationBranchRow extends Timestamps {
  id: string;
  iteration_id: string;
  repo: B2cRepo;
  branch: string;
  pr_url: string | null;
  merged_at: string | null;
}

export interface B2cTaskRow extends Timestamps {
  id: string;
  /** 'B2C-001' … minted by the database. */
  code: string;
  title: string;
  description: string | null;
  acceptance_md: string | null;
  notes: string | null;
  workstream_key: string;
  iteration_id: string | null;
  status: B2cTaskStatus;
  is_e2e_proof: boolean;
  priority: B2cTaskPriority;
  kind: B2cTaskKind;
  sort_order: number;
  branch_name: string | null;
  pr_url: string | null;
}

// --- Activity log (migration 0145) -------------------------------------------

export type B2cActivityEntity = "task" | "iteration" | "branch" | "workstream" | "stage";
export type B2cActivityAction = "create" | "update" | "delete";

/** One audit row, written only by the database triggers. */
export interface B2cActivityRow {
  id: string;
  /** Keyset cursor and total order (newest = highest). */
  seq: number;
  entity_type: B2cActivityEntity;
  entity_id: string;
  task_id: string | null;
  workstream_key: string | null;
  /** A snapshot label: "B2C-123", "G · iteration 2", "G · iteration 2 · yango-app", "G", "Stage 1". */
  subject: string;
  action: B2cActivityAction;
  /** The changed field for an update; null otherwise. */
  field: string | null;
  old_value: string | null;
  new_value: string | null;
  actor: string;
  summary: string;
  created_at: string;
}
